import { Toolbar, ToolbarButton, ToolbarGroup } from "@fluentui/react-components";
import {
  AddRegular,
  ArrowClockwise24Regular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
  PlayRegular,
  PauseRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

// Business logic
import { useSelectedMailboxesContext } from "../context/SelectedMailboxContext";
import { useStartMonitor, useStopMonitor } from "../hooks/useMailboxMutations";

const MailboxesToolbar = ({ setIsAddOpen, setIsEditOpen, setIsRemoverOpen }) => {
  const { t } = useTranslation();
  const { value: selectedMailboxes, setValue: setSelectedMailboxes } = useSelectedMailboxesContext();
  const queryClient = useQueryClient();

  const startMonitorMutation = useStartMonitor(["mailboxes"]);
  const stopMonitorMutation = useStopMonitor(["mailboxes"]);

  const handleClickNew = () => {
    setIsAddOpen(true);
  };

  const handleClickEdit = () => setIsEditOpen(true);

  const handleStartMonitor = () => {
    const mailboxId = selectedMailboxes?.[0]?.public_id;
    if (mailboxId) {
      startMonitorMutation.mutate(mailboxId);
    }
  };

  const handleStopMonitor = () => {
    const mailboxId = selectedMailboxes?.[0]?.public_id;
    if (mailboxId) {
      stopMonitorMutation.mutate(mailboxId);
    }
  };

  return (
    <Toolbar>
      <ToolbarGroup>
        <ToolbarButton icon={<AddRegular />} aria-label="Add" onClick={handleClickNew}>
          {t("generic.add")}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(selectedMailboxes?.length === 1)}
          icon={<EditRegular />}
          aria-label="Edit"
          onClick={handleClickEdit}
        >
          {t("generic.edit")}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(selectedMailboxes?.length === 1)}
          icon={<DeleteRegular />}
          aria-label="Delete"
          onClick={() => setIsRemoverOpen(true)}
        >
          {t("generic.delete")}
        </ToolbarButton>
        <ToolbarButton
          icon={<ArrowClockwise24Regular />}
          onClick={() => queryClient?.invalidateQueries({ queryKey: ["mailboxes"] })}
        >
          {t("generic.refresh")}
        </ToolbarButton>
        <ToolbarButton
          disabled={
            !(selectedMailboxes?.length === 1 && !selectedMailboxes[0]?.active) || startMonitorMutation.isPending
          }
          icon={<PlayRegular />}
          onClick={handleStartMonitor}
        >
          {t("generic.start", { defaultValue: "Start" })}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(selectedMailboxes?.length === 1 && selectedMailboxes[0]?.active) || stopMonitorMutation.isPending}
          icon={<PauseRegular />}
          onClick={handleStopMonitor}
        >
          {t("generic.stop", { defaultValue: "Stop" })}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(selectedMailboxes?.length > 0)}
          icon={<DismissRegular />}
          onClick={() => setSelectedMailboxes([])}
        >
          {t("generic.unselect")}
        </ToolbarButton>
      </ToolbarGroup>
      {selectedMailboxes && <span>{selectedMailboxes.name}</span>}
    </Toolbar>
  );
};

export default MailboxesToolbar;
